"use client";

/**
 * DED-1507: Documents Combobox Component
 * 
 * Multi-select combobox for linking existing documents.
 * Based on AssetsCombobox pattern.
 */
import * as React from "react";
import { Check, X, Search, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { getDocuments } from '@/services/documentsService';
import { useTenancy } from '@/contexts/TenancyContext';

interface Document {
  id: string;
  name: string;
  type?: string;
  fileName?: string;
  displayName?: string;
}

interface DocumentsComboboxProps {
  selected: string[];
  setSelected: (values: string[] | ((prev: string[]) => string[])) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  onDocumentSelect?: (docId: string) => void;
  onDocumentDeselect?: (docId: string) => void;
  excludeIds?: string[]; // Documents to exclude from the list (already linked)
}

interface DocumentsContextType {
  documents: Document[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<Document[]>;
}

const DocumentsContext = React.createContext<DocumentsContextType>({
  documents: [],
  loading: false,
  error: null,
  refresh: async () => [],
});

interface DocumentsProviderProps {
  children: React.ReactNode;
}

export const DocumentsProvider: React.FC<DocumentsProviderProps> = ({ children }) => {
  const [documents, setDocuments] = React.useState<Document[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [initialized, setInitialized] = React.useState<boolean>(false);
  const { activeTenancy } = useTenancy();

  const fetchDocuments = React.useCallback(async (): Promise<Document[]> => {
    if (!activeTenancy?.id || activeTenancy.id === 'Loading...') {
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const docs = await getDocuments(activeTenancy.id);
      
      const mappedDocs: Document[] = (docs || []).map((doc: any) => ({
        id: doc.docID || doc.id,
        name: doc.displayName || doc.fileName || doc.title || 'Untitled',
        type: doc.documentType,
        fileName: doc.fileName,
        displayName: doc.displayName,
      }));

      setDocuments(mappedDocs);
      setInitialized(true);
      return mappedDocs;
    } catch (err) {
      console.error('[DocumentsProvider] Error fetching documents:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch documents'));
      return [];
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id]);

  React.useEffect(() => {
    if (!initialized && activeTenancy?.id && activeTenancy.id !== 'Loading...') {
      fetchDocuments();
    }
  }, [activeTenancy?.id, initialized, fetchDocuments]);

  const value = React.useMemo(() => ({
    documents,
    loading,
    error,
    refresh: fetchDocuments,
  }), [documents, loading, error, fetchDocuments]);

  return (
    <DocumentsContext.Provider value={value}>
      {children}
    </DocumentsContext.Provider>
  );
};

export const useDocuments = () => React.useContext(DocumentsContext);

export const DocumentsCombobox: React.FC<DocumentsComboboxProps> = ({
  selected,
  setSelected,
  placeholder = "Select documents...",
  className,
  fullWidth = false,
  onDocumentSelect,
  onDocumentDeselect,
  excludeIds = [],
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const { documents, loading, refresh } = useDocuments();

  // Filter out excluded documents and apply search
  const filteredDocuments = React.useMemo(() => {
    let filtered = documents.filter(doc => !excludeIds.includes(doc.id));
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(query) ||
        doc.fileName?.toLowerCase().includes(query) ||
        doc.type?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [documents, excludeIds, searchQuery]);

  const handleSelect = (docId: string) => {
    if (selected.includes(docId)) {
      setSelected(selected.filter(id => id !== docId));
      onDocumentDeselect?.(docId);
    } else {
      setSelected([...selected, docId]);
      onDocumentSelect?.(docId);
    }
  };

  const handleRemove = (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(selected.filter(id => id !== docId));
    onDocumentDeselect?.(docId);
  };

  const getDocumentName = (docId: string) => {
    const doc = documents.find(d => d.id === docId);
    return doc?.name || docId;
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between min-h-[40px] h-auto",
            fullWidth ? "w-full" : "w-[300px]",
            className
          )}
        >
          <div className="flex flex-wrap gap-1 items-center">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selected.map(docId => (
                <Badge
                  key={docId}
                  variant="secondary"
                  className="flex items-center gap-1 text-xs"
                >
                  <FileText className="h-3 w-3" />
                  <span className="max-w-[150px] truncate">{getDocumentName(docId)}</span>
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={(e) => handleRemove(docId, e)}
                  />
                </Badge>
              ))
            )}
          </div>
          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className={cn("p-0", fullWidth ? "w-[var(--radix-popover-trigger-width)]" : "w-[300px]")} 
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={true}
        collisionPadding={8}
        // DED-1507: Ensure popover can extend beyond dialog boundaries for proper scrolling
        style={{ maxHeight: '300px', overscrollBehavior: 'contain' }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search documents by name..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList 
            className="max-h-[250px] overflow-y-auto overflow-x-hidden touch-pan-y"
            onWheel={(e) => {
              // DED-1507: Prevent wheel events from propagating to parent elements (fixes scroll issue)
              e.stopPropagation();
              const target = e.currentTarget;
              target.scrollTop += e.deltaY;
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? `No documents matching "${searchQuery}"` : 'No documents available'}
                </p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => refresh()}
                  className="mt-2"
                >
                  Refresh list
                </Button>
              </div>
            ) : (
              <CommandGroup>
                {filteredDocuments.map((doc) => (
                  <CommandItem
                    key={doc.id}
                    value={`${doc.name} ${doc.fileName || ''} ${doc.type || ''}`}
                    onSelect={() => handleSelect(doc.id)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-sm border shrink-0",
                      selected.includes(doc.id)
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-muted"
                    )}>
                      {selected.includes(doc.id) && <Check className="h-3 w-3" />}
                    </div>
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="truncate font-medium">{doc.name}</span>
                      {doc.type && (
                        <span className="text-xs text-muted-foreground">{doc.type}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default DocumentsCombobox;
